import React from 'react';
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    BlockRounded,
    CategoryRounded,
    CheckCircleRounded,
    InfoOutlined,
    WarningAmberRounded,
} from "@mui/icons-material";
import { CreditUsageCategoryList } from "../../../helper/enums/CreditUsageCategoryEnum";

const _CreditRestrictionsPicker = ({
    usageCategories,
    handleCategoryToggle,
    blockedSportIds,
    setBlockedSportIds,
    sportsList,
}) => {
    const isSportIncluded = usageCategories.includes("SPORT");

    return (
        <>
            {/* Usage Categories Section */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                    <CategoryRounded sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        دسته‌بندی‌های مجاز مصرف
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {CreditUsageCategoryList.map((cat) => {
                        const isSelected = usageCategories.includes(cat.code);
                        const isDisabled = !cat.active;

                        return (
                            <Tooltip
                                key={cat.code}
                                title={isDisabled ? (cat.badge || "به‌زودی — در حال حاضر غیرفعال") : (cat.description || cat.label)}
                            >
                                <span>
                                    <Chip
                                        label={cat.label + (isDisabled ? " (به‌زودی)" : "")}
                                        clickable={!isDisabled}
                                        disabled={isDisabled}
                                        onClick={!isDisabled ? () => handleCategoryToggle(cat.code) : undefined}
                                        color={isSelected ? "primary" : "default"}
                                        variant={isSelected ? "filled" : "outlined"}
                                        icon={isSelected ? <CheckCircleRounded fontSize="small" /> : undefined}
                                        sx={{
                                            fontWeight: 700,
                                            borderRadius: 2.5,
                                            py: 2,
                                            px: 0.5,
                                            borderWidth: isSelected ? 2 : 1,
                                            opacity: isDisabled ? 0.45 : 1,
                                            cursor: isDisabled ? 'not-allowed !important' : 'pointer',
                                        }}
                                    />
                                </span>
                            </Tooltip>
                        );
                    })}
                </Box>

                <Alert severity="info" icon={<InfoOutlined fontSize="small" />} sx={{ mt: 1.5, py: 0.3, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        💡 در حال حاضر فقط اعتبار «ورزش» فعال است؛ سایر حوزه‌ها به‌زودی فعال خواهند شد.
                    </Typography>
                </Alert>
            </Box>

            {/* Blocked Sports Section */}
            <Box sx={{ opacity: isSportIncluded ? 1 : 0.45, pointerEvents: isSportIncluded ? 'auto' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                    <BlockRounded sx={{ color: 'error.main', fontSize: 18 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        ورزش‌های غیرمجاز (اختیاری)
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                    ورزش‌های انتخابی با این اعتبار قابل رزرو نخواهند بود.
                </Typography>

                <FormControl fullWidth size="small">
                    <InputLabel id="blocked-sports-select-label">انتخاب ورزش‌های ممنوعه</InputLabel>
                    <Select
                        labelId="blocked-sports-select-label"
                        multiple
                        value={blockedSportIds}
                        onChange={(e) => setBlockedSportIds(e.target.value)}
                        input={<OutlinedInput label="انتخاب ورزش‌های ممنوعه" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((val) => {
                                    const sport = sportsList.find(s => String(s.Id) === String(val));
                                    return (
                                        <Chip
                                            key={val}
                                            label={sport?.Name || val}
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            onDelete={() => setBlockedSportIds(blockedSportIds.filter(id => String(id) !== String(val)))}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    >
                        {sportsList.map((sport) => (
                            <MenuItem key={sport.Id} value={sport.Id}>
                                <Checkbox checked={blockedSportIds.some(id => String(id) === String(sport.Id))} />
                                <Typography variant="body2">{sport.Name}</Typography>
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {blockedSportIds.length === 0
                            ? "تمامی ورزش‌ها مجاز هستند (پیش‌فرض)"
                            : `${blockedSportIds.length} ورزش مسدود شده است.`}
                    </FormHelperText>
                </FormControl>

                {blockedSportIds.length > 0 && (
                    <Alert severity="warning" icon={<WarningAmberRounded fontSize="small" />} sx={{ mt: 1.5, py: 0.5, borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            ⚠️ توجه: اگر بلیط کارمند شامل چند ورزش باشد و حتی <strong>یکی</strong> از آن‌ها در لیست ممنوع قرار داشته باشد، با این اعتبار قابل پرداخت نخواهد بود.
                        </Typography>
                    </Alert>
                )}
            </Box>
        </>
    );
};

export default _CreditRestrictionsPicker;
